import { PLAN_MONTHLY_CREDITS, type BillingInterval, type PaidPlan } from '../../shared/billing';

export type PlanId = PaidPlan;
export type { BillingInterval };

export type PlanDefinition = {
  id: PlanId;
  name: string;
  monthlyPrice: number;
  yearlyMonthlyPrice: number;
  credits: number;
  audience: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
};

export const plans: PlanDefinition[] = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 9,
    yearlyMonthlyPrice: 7,
    credits: PLAN_MONTHLY_CREDITS.starter,
    audience: 'Personal and occasional work',
    description: 'For personal photos, portfolios and light client work without paying for unused volume.',
    features: [
      'All 17 AI workflows + 8 free utilities',
      '250-credit monthly allowance',
      'Adjustable professional-detail enhancement',
      'Original-size, 2× and 4× enhancement output',
      'Uploads up to 50 MB',
      'Full-resolution downloads',
      'Up to 240 MP supported result size'
    ]
  },
  {
    id: 'creator',
    name: 'Creator',
    monthlyPrice: 19,
    yearlyMonthlyPrice: 15,
    credits: PLAN_MONTHLY_CREDITS.creator,
    audience: 'Regular creative production',
    description: 'For photographers, designers, sellers and creators processing images throughout the week.',
    features: [
      'Everything in Starter',
      '750-credit monthly allowance',
      '2×, 4× and 8× output access',
      'Size-aware credits for large files',
      'Expanded result size up to 320 MP',
      'Full-resolution downloads and job history',
      'Email support for account and processing issues'
    ],
    highlighted: true,
    badge: 'Best value'
  },
  {
    id: 'studio',
    name: 'Studio',
    monthlyPrice: 39,
    yearlyMonthlyPrice: 31,
    credits: PLAN_MONTHLY_CREDITS.studio,
    audience: 'Agencies and higher volume',
    description: 'For agencies, stores and production teams that enhance and restore images every working day.',
    features: [
      'Everything in Creator',
      '1,800-credit monthly allowance',
      'Best fit for frequent 4× and 8× jobs',
      'Highest result ceiling up to 360 MP',
      'Business-ready receipts and tax details',
      'Self-service subscription management',
      'Billing and technical support by email'
    ]
  }
];

export function getPlan(planId: string | null | undefined) {
  return plans.find((plan) => plan.id === planId) ?? plans[1];
}

export function getPlanPrice(plan: PlanDefinition, billing: BillingInterval) {
  return billing === 'yearly' ? plan.yearlyMonthlyPrice : plan.monthlyPrice;
}

export function getPlanCharge(plan: PlanDefinition, billing: BillingInterval) {
  return billing === 'yearly' ? plan.yearlyMonthlyPrice * 12 : plan.monthlyPrice;
}

export function formatPlanName(planId: string | null | undefined) {
  if (!planId || planId === 'free') return 'Free';
  return getPlan(planId).name;
}
